import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    SimpleGrid,
    Text,
    useBreakpointValue,
    useMediaQuery,
    useMultiStyleConfig,
    useStyleConfig,
} from '@chakra-ui/react'
import KeyFeaturesCard from './key-features-card'
import {Swiper, SwiperSlide} from 'swiper/react'
import {breakpoints, remCalc} from '../../../theme/_utils'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const KeyFeatures = ({title, description, cards, isSlider = false, aspectRatio}) => {
    const [isDesktop] = useMediaQuery(`(min-width: ${breakpoints.lg})`)
    const categoriesData = useGetCategoriesData()
    const ids = cards
        ?.filter((c) => c.button?.productsfcc?.data?.length > 0)
        ?.map((c) => c.button.productsfcc.data[0].id)
        ?.filter(Boolean)

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: ids?.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'key-features-index',
            },
        },
        {enabled: ids?.length > 0},
    )
    const slidesStyles = useBreakpointValue({
        base: {num: 1.2, gap: 8},
        md: {num: 2.3, gap: 24},
    })

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useMultiStyleConfig('KeyFeatures', {
        variant: isSlider ? 'slider' : 'default',
    })

    const {locale} = useMultiSite()

    return (
        <Flex
            {...styles.container}
            px={!isSlider ? ['small.base', 'small.base', 'small.md', 'small.large', 0] : 'initial'}
        >
            <SimpleGrid {...styles.headerGrid}>
                <Text {...styles.title}>{title}</Text>
                {description && <Text {...styles.description}>{description}</Text>}
            </SimpleGrid>
            {isSlider && !isDesktop ? (
                <Box
                    __css={{
                        ...swiperStyles,
                        '.swiper-slide': {...swiperStyles['.swiper-slide'], height: 'auto'},
                    }}
                >
                    <Swiper
                        slidesPerView={slidesStyles.num}
                        spaceBetween={slidesStyles.gap}
                        style={{paddingInline: remCalc(16)}}
                        className="key-features-slider"
                    >
                        {cards &&
                            cards.map((card) => (
                                <SwiperSlide key={card.uid}>
                                    <KeyFeaturesCard
                                        imageUrl={card?.image?.url}
                                        isSlider
                                        title={card?.header_copy}
                                        description={card?.body_copy}
                                        aspectRatio={aspectRatio}
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                </Box>
            ) : (
                <Flex {...styles.cardsContainer}>
                    {cards &&
                        cards.map((card) => {
                            const {url, label, isAbsolute} = getGlobalLink({
                                link: card?.button,
                                categoriesData,
                                productsData,
                                locale: locale?.alias || locale?.id?.toLowerCase(),
                            })

                            return (
                                <KeyFeaturesCard
                                    isLinkAbsolute={isAbsolute}
                                    key={card.uid}
                                    imageUrl={card?.image?.url}
                                    images={card?.images}
                                    title={card?.header_copy}
                                    description={card?.body_copy}
                                    linkLabel={label}
                                    linkUrl={url}
                                    aspectRatio={aspectRatio}
                                />
                            )
                        })}
                </Flex>
            )}
        </Flex>
    )
}

KeyFeatures.displayName = 'KeyFeatures'

KeyFeatures.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    cards: PropTypes.array,
    isSlider: PropTypes.bool,
    aspectRatio: PropTypes.string,
}

export default KeyFeatures
